import ReactMarkdown from 'react-markdown'

interface MarkdownProps {
    text: string
}

// Same-origin link: starts with `/` but NOT `//`. The `//host` form is a
// protocol-relative URL that resolves to a different origin and would bypass
// the external-link safety treatment below.
const isInternalHref = (href: string) => href.startsWith('/') && !href.startsWith('//')

const isSafeUrl = (href?: string) => {
    if (!href) return false
    if (isInternalHref(href)) return true
    if (href.startsWith('#')) return true
    try {
        const url = new URL(href, window.location.origin)
        return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'mailto:'
    } catch {
        return false
    }
}

// Renders an ```html block as a strictly-sandboxed preview. We use srcDoc so
// the content never touches our DOM, and we deliberately OMIT `allow-same-origin`
// from the sandbox so scripts in the preview cannot read cookies, hit our
// authenticated API, or otherwise act with the parent's privileges. Assistant
// output ingests untrusted tenant data (campaign names, custom fields,
// docs results) so we treat it as actively hostile.
const HtmlPreview = ({ content }: { content: string }) => (
    <iframe
        className="chat-html-preview-frame"
        srcDoc={content}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        title="Preview"
    />
)

export default function Markdown({ text }: MarkdownProps) {
    return (
        <ReactMarkdown
            // Images are blocked entirely: assistant output is untrusted, and an
            // attacker-controlled `![](url)` can fire authenticated GETs (CSRF-by-image),
            // exfiltrate the Referer, or load external trackers.
            disallowedElements={['img']}
            components={{
                a: ({ href, children }) => {
                    if (!isSafeUrl(href)) return <span>{children}</span>
                    const isInternal = href ? isInternalHref(href) : false
                    return (
                        <a
                            href={href}
                            target={isInternal ? undefined : '_blank'}
                            rel={isInternal ? undefined : 'noreferrer noopener'}
                            referrerPolicy={isInternal ? undefined : 'no-referrer'}
                        >
                            {children}
                        </a>
                    )
                },
                code: ({ className, children }) => {
                    const lang = /language-(\w+)/.exec(className ?? '')?.[1]
                    const content = String(children).replace(/\n$/, '')
                    if (lang === 'html') {
                        return <div className="chat-html-preview"><HtmlPreview content={content} /></div>
                    }
                    if (lang) {
                        return <pre className="chat-code-block"><code>{content}</code></pre>
                    }
                    return <code>{content}</code>
                },
                pre: ({ children }) => <>{children}</>,
            }}
        >
            {text}
        </ReactMarkdown>
    )
}
